import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import { Plus, Edit2, Building, Eye, ChevronUp, ChevronDown, X, KeyRound, Copy, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatAadhar, to12 } from '../utils/format';

const emptyForm = {
  name: '', gstNo: '', aadharCard: '', address: '', phone: '', email: '',
};

// Generate preview ID client-side (mirrors backend logic)
const previewOrgId = (form) => {
  const namePart = (form.name || '').replace(/\s+/g, '').substring(0, 3).toUpperCase();
  const gstPart = (form.gstNo || '').replace(/\s+/g, '').substring(0, 2).toUpperCase();
  const aadharPart = (form.aadharCard || '').replace(/\s+/g, '').substring(0, 2);
  const phonePart = (form.phone || '').replace(/\D/g, '').substring(0, 2);
  return `${namePart}${gstPart}${aadharPart}${phonePart}`;
};

const PAGE_SIZE = 50;

export default function OrganizationPage() {
  const { hasRole } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orgOutlets, setOrgOutlets] = useState({});
  const [page, setPage] = useState(1);
  const [credInfo, setCredInfo] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [outletsModalOrg, setOutletsModalOrg] = useState(null);
  const [viewOrg, setViewOrg] = useState(null);
  const [viewEditMode, setViewEditMode] = useState(false);
  const [viewForm, setViewForm] = useState({});
  const [viewSaving, setViewSaving] = useState(false);

  const canEdit = hasRole('super_admin', 'outlet_owner');
  const canDelete = hasRole('super_admin');

  const fetchOrgs = async () => {
    const res = await api.get('/organizations');
    setOrgs(res.data);
    setPage(1);
  };

  useEffect(() => { fetchOrgs(); }, []);

  const openOutletsModal = async (org) => {
    setOutletsModalOrg(org);
    // Always fetch the latest outlets for this organization
    const res = await api.get(`/outlets?organization=${org._id}`);
    setOrgOutlets((prev) => ({ ...prev, [org._id]: res.data }));
  };

  const openCreate = () => { setForm(emptyForm); setEditId(null); setShowModal(true); };
  const attemptCloseCreate = () => {
    if (window.confirm('Close this form? Any unsaved changes will be lost.')) {
      setShowModal(false);
    }
  };
  const openEdit = (o) => {
    setForm({
      name: o.name, gstNo: o.gstNo || '', aadharCard: formatAadhar(o.aadharCard) || '',
      address: o.address, phone: o.phone, email: o.email || '',
    });
    setEditId(o._id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) {
        await api.put(`/organizations/${editId}`, form);
        toast.success('Organization updated');
        setShowModal(false);
      } else {
        const res = await api.post('/organizations', form);
        const login = res.data.login;
        if (login?.emailSent) {
          toast.success('Organization created. Login credentials emailed.');
          setShowModal(false);
        } else if (login?.password) {
          // Email could not be sent — show the password to the admin
          setCredInfo({ email: login.email, password: login.password, note: login.note });
          setShowModal(false);
        } else {
          toast.success('Organization created');
          setShowModal(false);
        }
      }
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving organization');
    } finally {
      setLoading(false);
    }
  };

  const orgToForm = (o) => ({
    name: o.name, gstNo: o.gstNo || '', aadharCard: formatAadhar(o.aadharCard) || '',
    phone: o.phone, email: o.email || '', address: o.address,
  });

  const openView = (org) => {
    setViewOrg(org);
    setViewEditMode(false);
  };

  const startViewEdit = () => {
    setViewForm(orgToForm(viewOrg));
    setViewEditMode(true);
  };

  const isViewDirty = () =>
    viewEditMode && JSON.stringify(viewForm) !== JSON.stringify(orgToForm(viewOrg));

  const copyPassword = async () => {
    try {
      const res = await api.get(`/organizations/${viewOrg._id}/password`);
      await navigator.clipboard?.writeText(res.data.password);
      toast.success('Password copied to clipboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not copy password');
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Reset the login password for "${viewOrg.name}"?\n\nA new password will be generated and the old one will stop working.`)) return;
    try {
      const res = await api.post(`/organizations/${viewOrg._id}/reset-password`);
      if (res.data.emailSent) {
        toast.success('New password emailed to the organization.');
      } else {
        setCredInfo({ email: res.data.email, password: res.data.password, note: res.data.note });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error resetting password');
    }
  };

  // ----- Delete -----
  const downloadOrgData = async (org) => {
    const { data } = await api.get(`/organizations/${org._id}/export-data`);
    const { outlets, staff, attendance } = data;
    const todayStr = new Date().toISOString().slice(0, 10);

    const doc = new jsPDF();
    const navy = [13, 59, 102];
    const white = [255, 255, 255];
    const pageH = 287;
    let y = 20;

    const ensureSpace = (need = 8) => {
      if (y + need > pageH) { doc.addPage(); y = 20; }
    };
    const heading = (text) => {
      ensureSpace(12);
      doc.setFontSize(12); doc.setFont('helvetica', 'bold');
      doc.setTextColor(...navy);
      doc.text(text, 14, y);
      y += 7;
      doc.setDrawColor(228, 237, 246); doc.setLineWidth(0.5);
      doc.line(14, y - 4, 196, y - 4);
    };
    const line = (label, value) => {
      ensureSpace(7);
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(String(label), 16, y);
      doc.setTextColor(15, 23, 42); doc.setFont('helvetica', 'bold');
      doc.text(String(value), 90, y);
      y += 7;
    };

    doc.setFillColor(...navy);
    doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('CloudStay', 14, 12);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Organization Data Export', 14, 21);
    doc.text(`Generated: ${todayStr}`, 196, 21, { align: 'right' });
    y = 38;

    heading('Organization Profile');
    line('Organization ID', org.orgId);
    line('Organization Name', org.name);
    line('GST Number', org.gstNo || '—');
    line('Aadhar Card', formatAadhar(org.aadharCard) || '—');
    line('Mobile', org.phone ? `+91 ${org.phone}` : '—');
    line('Email', org.email || '—');
    line('Address', org.address || '—');
    line('Status', org.isActive === false ? 'Inactive' : 'Active');
    y += 4;

    heading(`Outlets (${outlets.length})`);
    if (outlets.length) {
      outlets.forEach((o) => {
        line(o.outletId, `${o.name} — ${o.address || '—'} — ${o.phone ? `+91 ${o.phone}` : '—'}`);
      });
    } else {
      line('—', 'No outlets');
    }
    y += 4;

    heading(`Staff (${staff.length})`);
    if (staff.length) {
      staff.forEach((s) => {
        line(s.staffId, `${s.name} — ${s.outlet?.name || '—'} — Rs ${s.salary?.toLocaleString()}`);
      });
    } else {
      line('—', 'No staff');
    }
    y += 4;

    heading(`Attendance Records (${attendance.length})`);
    if (attendance.length) {
      ensureSpace(7);
      doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text('Date', 16, y);
      doc.text('Staff ID', 50, y);
      doc.text('Time In', 80, y);
      doc.text('Time Out', 110, y);
      doc.text('Hours', 140, y);
      doc.text('Status', 165, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      const staffById = Object.fromEntries(staff.map((s) => [s._id, s.staffId]));
      attendance.forEach((a) => {
        ensureSpace(6);
        doc.text(a.date?.split('T')[0] || '—', 16, y);
        doc.text(staffById[a.staff] || '—', 50, y);
        doc.text(to12(a.timeIn) || '—', 80, y);
        doc.text(to12(a.timeOut) || '—', 110, y);
        doc.text(String(a.hoursWorked || 0), 140, y);
        doc.text(a.status || '—', 165, y);
        y += 6;
      });
    } else {
      line('—', 'No attendance records');
    }

    doc.save(`organization_data_${org.orgId}_${org.name.replace(/\s+/g, '_')}.pdf`);
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`Delete "${org.name}" (${org.orgId})?\n\nThis will permanently delete ALL its outlets, staff and attendance records. This action cannot be undone.`)) return;
    if (!window.confirm('Download this organization\'s data before deleting?\n\nOK = Download data & Delete   |   Cancel = Cancel')) return;
    try {
      await downloadOrgData(org);
      await api.delete(`/organizations/${org._id}`);
      toast.success('Organization deleted');
      setViewOrg(null);
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting organization');
    }
  };

  const attemptCloseView = () => {
    if (isViewDirty()) {
      const save = window.confirm('You have unsaved changes. Do you want to save them?\n\nOK = Save   |   Cancel = Discard');
      if (save) { saveViewEdit(); return; }
    }
    setViewOrg(null);
    setViewEditMode(false);
  };

  const saveViewEdit = async () => {
    setViewSaving(true);
    try {
      const res = await api.put(`/organizations/${viewOrg._id}`, viewForm);
      toast.success('Organization updated');
      setViewOrg(res.data);
      setViewEditMode(false);
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating');
    } finally {
      setViewSaving(false);
    }
  };

  const handleToggle = async (org) => {
    const turningOff = org.isActive;
    const msg = turningOff
      ? 'Turn OFF this organization? All its outlets will be deactivated until it is turned back on.'
      : 'Turn ON this organization? All its outlets will be reactivated.';
    if (!window.confirm(msg)) return;
    try {
      const res = await api.patch(`/organizations/${org._id}/toggle`);
      toast.success(res.data.isActive ? 'Organization activated' : 'Organization deactivated');
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating status');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const filteredOrgs = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return orgs;
    return orgs.filter((o) => {
      const fields = [
        o.orgId, o.name, o.gstNo, o.aadharCard,
        o.phone, o.phone ? `+91 ${o.phone}` : '', o.phone ? `+91${o.phone}` : '',
        o.email, o.address,
      ];
      return fields.filter(Boolean).some((v) => String(v).toLowerCase().includes(query));
    });
  }, [orgs, search]);

  const sortedOrgs = React.useMemo(() => {
    if (!sortBy) return filteredOrgs;
    const arr = [...filteredOrgs];
    arr.sort((a, b) => {
      let av, bv;
      if (sortBy === 'outlets') { av = a.outletCount || 0; bv = b.outletCount || 0; }
      else if (sortBy === 'status') { av = a.isActive === false ? 0 : 1; bv = b.isActive === false ? 0 : 1; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredOrgs, sortBy, sortDir]);

  const SortArrows = ({ field }) => (
    <span className="sort-arrows">
      <ChevronUp size={11} className={sortBy === field && sortDir === 'asc' ? 'active' : ''} />
      <ChevronDown size={11} className={sortBy === field && sortDir === 'desc' ? 'active' : ''} />
    </span>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Organizations</h2>
          <p className="text-muted">List of organizations - {orgs.length}</p>
        </div>
        <div className="header-actions">
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search organizations by name, ID, GST, mobile, email..."
            />
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} /> Add Organization
            </button>
          )}
        </div>
      </div>

      <div className="table-container table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Org ID</th>
              <th>ORG NAME</th>
              <th>Mobile</th>
              <th>Email</th>
              <th className="th-sortable" onClick={() => handleSort('outlets')}>
                <span className="th-sort-inner">Outlets <SortArrows field="outlets" /></span>
              </th>
              <th>View Details</th>
              {canEdit && (
                <th className="th-sortable" onClick={() => handleSort('status')}>
                  <span className="th-sort-inner">Status <SortArrows field="status" /></span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedOrgs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((org) => (
                <tr key={org._id} className={org.isActive === false ? 'row-disabled' : ''}>
                  <td><span className="badge outlet-id-badge">{org.orgId}</span></td>
                  <td><strong>{org.name}</strong></td>
                  <td>{org.phone ? `+91 ${org.phone}` : '—'}</td>
                  <td>{org.email || '—'}</td>
                  <td>
                    <button
                      className="outlet-count-btn"
                      onClick={() => openOutletsModal(org)}
                      title="View outlets"
                    >
                      {org.outletCount ?? 0}
                    </button>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => openView(org)}>
                      <Eye size={13} /> View
                    </button>
                  </td>
                  {canEdit && (
                    <td>
                      <button
                        type="button"
                        className={`pill-toggle ${org.isActive === false ? 'off' : 'on'}`}
                        onClick={() => handleToggle(org)}
                        title={org.isActive === false ? 'Turn ON (activate)' : 'Turn OFF (deactivate)'}
                        aria-pressed={org.isActive !== false}
                      >
                        <span className="pill-toggle-label">{org.isActive === false ? 'OFF' : 'ON'}</span>
                        <span className="pill-toggle-knob" />
                      </button>
                    </td>
                  )}
                </tr>
            ))}

            {sortedOrgs.length === 0 && (
              <tr><td colSpan={canEdit ? 7 : 6} className="empty-state">
                {orgs.length === 0 ? 'No organizations yet. Create your first organization.' : 'No organizations match your search.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {sortedOrgs.length > PAGE_SIZE && (
        <div className="pagination">
          <span className="pagination-info">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, sortedOrgs.length)}–{Math.min(page * PAGE_SIZE, sortedOrgs.length)} of {sortedOrgs.length}
          </span>
          <div className="pagination-controls">
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >«</button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1}
            >‹ Prev</button>

            {Array.from({ length: Math.ceil(sortedOrgs.length / PAGE_SIZE) }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === Math.ceil(sortedOrgs.length / PAGE_SIZE) || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setPage(p)}
                  >{p}</button>
                )
              )}

            <button
              className="btn btn-sm btn-outline"
              onClick={() => setPage((p) => p + 1)}
              disabled={page === Math.ceil(sortedOrgs.length / PAGE_SIZE)}
            >Next ›</button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setPage(Math.ceil(sortedOrgs.length / PAGE_SIZE))}
              disabled={page === Math.ceil(sortedOrgs.length / PAGE_SIZE)}
            >»</button>
          </div>
        </div>
      )}

      {/* Credentials fallback modal (shown when email could not be sent) */}
      {credInfo && (
        <div className="modal-overlay" onClick={() => setCredInfo(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Organization Login Created</h3>
            <p className="info-banner">{credInfo.note}</p>
            <div className="staff-detail-view">
              <div className="detail-row">
                <span className="detail-label">Login Email</span>
                <span className="detail-value">{credInfo.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Password</span>
                <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 700 }}>{credInfo.password}</span>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-outline"
                onClick={() => { navigator.clipboard?.writeText(`Email: ${credInfo.email}\nPassword: ${credInfo.password}`); toast.success('Copied'); }}
              >
                Copy
              </button>
              <button className="btn btn-primary" onClick={() => setCredInfo(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Outlets Modal */}
      {outletsModalOrg && (
        <div className="modal-overlay" onClick={() => setOutletsModalOrg(null)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <h3>Outlets — {outletsModalOrg.name}</h3>
            {orgOutlets[outletsModalOrg._id] === undefined ? (
              <p className="text-muted no-outlets">Loading...</p>
            ) : orgOutlets[outletsModalOrg._id].length > 0 ? (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Outlet ID</th>
                      <th>Outlet Name</th>
                      <th>Address</th>
                      <th>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgOutlets[outletsModalOrg._id].map((outlet) => (
                      <tr key={outlet._id}>
                        <td><span className="badge outlet-id-badge">{outlet.outletId}</span></td>
                        <td>{outlet.name}</td>
                        <td>{outlet.address}</td>
                        <td>{outlet.phone}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted no-outlets">No outlets linked to this organization yet.</p>
            )}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setOutletsModalOrg(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewOrg && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Organization Details</h3>
              <div className="modal-header-actions">
                {canEdit && !viewEditMode && (
                  <button className="btn btn-sm btn-outline btn-icon" onClick={startViewEdit} title="Edit">
                    <Edit2 size={15} />
                  </button>
                )}
                <button className="btn btn-sm btn-outline btn-icon" onClick={attemptCloseView} title="Close">
                  <X size={16} />
                </button>
              </div>
            </div>

            {!viewEditMode ? (
              /* ---------- READ-ONLY VIEW ---------- */
              <>
                <div className="staff-detail-view">
                  {[
                    ['Organization ID', viewOrg.orgId],
                    ['Organization Name', viewOrg.name],
                    ['GST Number', viewOrg.gstNo],
                    ['Aadhar Card', formatAadhar(viewOrg.aadharCard)],
                    ['Mobile Number', viewOrg.phone ? `+91 ${viewOrg.phone}` : ''],
                    ['Email', viewOrg.email],
                    ['Address', viewOrg.address],
                    ['Status', viewOrg.isActive ? 'Active' : 'Inactive'],
                    ['Created On', viewOrg.createdAt && new Date(viewOrg.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="detail-row">
                      <span className="detail-label">{label}</span>
                      <span className="detail-value">{value}</span>
                    </div>
                  ))}
                  {canDelete && (
                    <div className="detail-row">
                      <span className="detail-label">Delete organization</span>
                      <span className="detail-value">
                        <a href="#delete" className="link-danger" onClick={(e) => { e.preventDefault(); handleDeleteOrg(viewOrg); }}>
                          Delete
                        </a>
                      </span>
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  {canEdit && (
                    <button className="btn btn-outline" onClick={copyPassword} title="Copy the current login password">
                      <Copy size={15} /> Copy Password
                    </button>
                  )}
                  {canEdit && (
                    <button className="btn btn-outline" onClick={resetPassword} title="Generate a new login password">
                      <KeyRound size={15} /> Reset Password
                    </button>
                  )}
                  <button className="btn btn-outline" onClick={attemptCloseView}>Close</button>
                </div>
              </>
            ) : (
              /* ---------- EDIT MODE ---------- */
              <form onSubmit={(e) => { e.preventDefault(); saveViewEdit(); }}>
                <div className="form-group">
                  <label>Organization ID</label>
                  <input value={viewOrg.orgId} readOnly className="input-readonly" />
                </div>
                <div className="form-group">
                  <label>Organization Name *</label>
                  <input value={viewForm.name} onChange={(e) => setViewForm({ ...viewForm, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>GST Number</label>
                  <input value={viewForm.gstNo} onChange={(e) => setViewForm({ ...viewForm, gstNo: e.target.value.toUpperCase().slice(0, 15) })} maxLength={15} />
                </div>
                <div className="form-group">
                  <label>Aadhar Card</label>
                  <input value={viewForm.aadharCard} onChange={(e) => setViewForm({ ...viewForm, aadharCard: e.target.value.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1-') })} maxLength={14} inputMode="numeric" placeholder="0000-0000-0000" />
                </div>
                <div className="form-group">
                  <label>Mobile Number *</label>
                  <div className="input-group">
                    <span className="input-prefix">+91</span>
                    <input value={viewForm.phone} onChange={(e) => setViewForm({ ...viewForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} maxLength={10} inputMode="numeric" required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email * <span className="text-muted">(login — cannot be changed)</span></label>
                  <input type="email" value={viewForm.email} readOnly className="input-readonly" />
                </div>
                <div className="form-group">
                  <label>Address *</label>
                  <textarea value={viewForm.address} onChange={(e) => setViewForm({ ...viewForm, address: e.target.value })} rows={2} required />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={attemptCloseView}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={viewSaving}>
                    {viewSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>{editId ? 'Edit Organization' : 'Create Organization'}</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={attemptCloseCreate} title="Close">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="form-grid">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Organization Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>GST Number</label>
                <input
                  value={form.gstNo}
                  onChange={(e) => setForm({ ...form, gstNo: e.target.value.toUpperCase().slice(0, 15) })}
                  maxLength={15}
                />
              </div>

              <div className="form-group">
                <label>Aadhar Card Number</label>
                <input
                  value={form.aadharCard}
                  onChange={(e) => setForm({ ...form, aadharCard: e.target.value.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1-') })}
                  maxLength={14}
                  inputMode="numeric"
                  placeholder="0000-0000-0000"
                />
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Address *</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2}
                  required
                />
              </div>

              <div className="form-group">
                <label>Mobile Number *</label>
                <div className="input-group">
                  <span className="input-prefix">+91</span>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    maxLength={10}
                    inputMode="numeric"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email * <span className="text-muted">(used as login)</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>


              <div className="modal-actions" style={{ gridColumn: '1 / -1' }}>
                <button type="button" className="btn btn-outline" onClick={attemptCloseCreate}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editId ? 'Update' : 'Create Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
